import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../config/api';
import {
  SparklesIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserGroupIcon,
  QuestionMarkCircleIcon,
  BoltIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';

interface AgentInsightsPageProps {
  user: any;
  coachMode?: boolean;
  clientId?: number;
  clientName?: string;
}

// --- Helpers ---

function scoreColor(score: number | null): string {
  if (score === null || score === undefined) return 'text-gray-400';
  if (score >= 80) return 'text-green-600';
  if (score >= 50) return 'text-yellow-600';
  return 'text-red-600';
}

function scoreBgColor(score: number | null): string {
  if (score === null || score === undefined) return 'bg-gray-200';
  if (score >= 80) return 'bg-green-500';
  if (score >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
}

function statusBadge(status: string | null) {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    engaged:            { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Engaged' },
    engaged_with_gaps:  { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Gaps' },
    needs_attention:    { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Needs Attention' },
    inactive:           { bg: 'bg-red-100',    text: 'text-red-800',    label: 'Inactive' },
  };
  const s = status ? map[status] : null;
  if (!s) return <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>;
  return <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${s.bg} ${s.text}`}>{s.label}</span>;
}

function nudgeTypeBadge(type: string) {
  const map: Record<string, { bg: string; text: string }> = {
    encouragement: { bg: 'bg-green-100', text: 'text-green-800' },
    suggestion:    { bg: 'bg-blue-100',  text: 'text-blue-800' },
    alert:         { bg: 'bg-red-100',   text: 'text-red-800' },
  };
  const s = map[type] || { bg: 'bg-gray-100', text: 'text-gray-700' };
  return <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${s.bg} ${s.text}`}>{type}</span>;
}

function formatDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// --- Mode A: All-Clients Overview ---

const AllClientsOverview: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['coach-briefings'],
    queryFn: () => apiClient.getCoachBriefings(),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="bg-gray-200 rounded-lg h-32 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="bg-gray-200 rounded-lg h-24 animate-pulse" />)}
        </div>
        <div className="bg-gray-200 rounded-lg h-64 animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <ExclamationTriangleIcon className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <p className="text-red-700 font-medium">Failed to load insights</p>
          <p className="text-red-500 text-sm mt-1">{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  const clients = data?.clients || [];

  // Compute summary buckets
  const engaged = clients.filter(c => c.engagementScore !== null && c.engagementScore >= 80);
  const moderate = clients.filter(c => c.engagementScore !== null && c.engagementScore >= 50 && c.engagementScore < 80);
  const atRisk = clients.filter(c => c.engagementScore !== null && c.engagementScore < 50);
  const noData = clients.filter(c => c.engagementScore === null);

  // Sort by engagement score ascending (at-risk first), nulls last
  const sorted = [...clients].sort((a, b) => {
    if (a.engagementScore === null && b.engagementScore === null) return 0;
    if (a.engagementScore === null) return 1;
    if (b.engagementScore === null) return -1;
    return a.engagementScore - b.engagementScore;
  });

  // Empty state
  if (clients.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <SparklesIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Insights Yet</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Agent insights are generated daily. Once your clients have activity data, briefings will appear here automatically.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Gradient Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-sm">
        <div className="px-6 py-8 sm:px-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-white">Client Insights</h1>
              <p className="mt-2 text-blue-100">AI-generated briefings and engagement tracking</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-white">{clients.length}</div>
              <div className="text-blue-100 text-sm">total clients</div>
            </div>
          </div>
        </div>
      </div>

      {/* 4 Summary Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Engaged</p>
              <p className="text-2xl font-semibold text-gray-900">{engaged.length}</p>
              <p className="text-xs text-gray-400">Score 80+</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <BoltIcon className="h-8 w-8 text-yellow-500 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Moderate</p>
              <p className="text-2xl font-semibold text-gray-900">{moderate.length}</p>
              <p className="text-xs text-gray-400">Score 50–79</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <ShieldExclamationIcon className="h-8 w-8 text-red-500 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">At Risk</p>
              <p className="text-2xl font-semibold text-gray-900">{atRisk.length}</p>
              <p className="text-xs text-gray-400">Score &lt;50</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <QuestionMarkCircleIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">No Data Yet</p>
              <p className="text-2xl font-semibold text-gray-900">{noData.length}</p>
              <p className="text-xs text-gray-400">Awaiting insights</p>
            </div>
          </div>
        </div>
      </div>

      {/* Client Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Client Briefings</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Engagement Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Insight</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Flags</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sorted.map((client) => {
                const briefing = client.coachBriefing;
                const flags: string[] = briefing?.flags || [];
                return (
                  <tr key={client.clientId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{client.clientName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {statusBadge(briefing?.status || null)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {client.engagementScore !== null ? (
                        <div className="flex items-center">
                          <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                            <div
                              className={`h-2 rounded-full ${scoreBgColor(client.engagementScore)}`}
                              style={{ width: `${Math.min(100, client.engagementScore)}%` }}
                            />
                          </div>
                          <span className={`text-sm font-medium ${scoreColor(client.engagementScore)}`}>
                            {client.engagementScore}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(client.insightDate)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {flags.length > 0 ? flags.map((flag, i) => (
                          <span key={i} className="inline-flex px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700">
                            {flag}
                          </span>
                        )) : (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// --- Mode B: Per-Client Detail ---

const ClientInsightDetail: React.FC<{ clientId: number; clientName: string }> = ({ clientId, clientName }) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['client-insight-detail', clientId],
    queryFn: () => apiClient.getClientInsightDetail(String(clientId)),
    staleTime: 5 * 60 * 1000,
  });

  const regenerateMutation = useMutation({
    mutationFn: () => apiClient.regenerateClientInsight(String(clientId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-insight-detail', clientId] });
      queryClient.invalidateQueries({ queryKey: ['coach-briefings'] });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="bg-gray-200 rounded-lg h-20 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-200 rounded-lg h-48 animate-pulse" />
          <div className="bg-gray-200 rounded-lg h-48 animate-pulse" />
        </div>
        <div className="bg-gray-200 rounded-lg h-40 animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <ExclamationTriangleIcon className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <p className="text-red-700 font-medium">Failed to load insight for {clientName}</p>
          <p className="text-red-500 text-sm mt-1">{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  const insight = data?.data;

  // Empty state — no insight for this client
  if (!insight) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <SparklesIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Insight for {clientName}</h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">
            No AI insight has been generated for this client yet. You can trigger one manually.
          </p>
          <button
            onClick={() => regenerateMutation.mutate()}
            disabled={regenerateMutation.isPending}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {regenerateMutation.isPending ? (
              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <SparklesIcon className="h-4 w-4 mr-2" />
            )}
            {regenerateMutation.isPending ? 'Generating...' : 'Generate Insight'}
          </button>
          {regenerateMutation.isError && (
            <p className="text-red-500 text-sm mt-3">{(regenerateMutation.error as Error).message}</p>
          )}
        </div>
      </div>
    );
  }

  const briefing = insight.coach_briefing || {};
  const nudges: any[] = Array.isArray(insight.user_nudges) ? insight.user_nudges : [];
  const engScore = insight.engagement_score;
  const dataQuality = insight.data_quality;
  const insightDate = insight.insight_date;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{clientName}</h2>
            <p className="text-sm text-gray-500 mt-1">Insight from {formatDate(insightDate)}</p>
          </div>
          <button
            onClick={() => regenerateMutation.mutate()}
            disabled={regenerateMutation.isPending}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {regenerateMutation.isPending ? (
              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ArrowPathIcon className="h-4 w-4 mr-2" />
            )}
            {regenerateMutation.isPending ? 'Regenerating...' : 'Regenerate'}
          </button>
        </div>
        {regenerateMutation.isError && (
          <p className="text-red-500 text-sm mt-2">{(regenerateMutation.error as Error).message}</p>
        )}
        {regenerateMutation.isSuccess && (
          <p className="text-green-600 text-sm mt-2">Insight regenerated successfully.</p>
        )}
      </div>

      {/* Engagement Score + Coach Briefing row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Engagement Score Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-4">Engagement Score</h3>
          <div className="text-center">
            <span className={`text-5xl font-bold ${scoreColor(engScore)}`}>
              {engScore !== null && engScore !== undefined ? engScore : '—'}
            </span>
            <p className="text-xs text-gray-400 mt-2">out of 100</p>
          </div>
          {/* Score bar */}
          {engScore !== null && engScore !== undefined && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full ${scoreBgColor(engScore)}`}
                  style={{ width: `${Math.min(100, engScore)}%` }}
                />
              </div>
            </div>
          )}
          {/* Data quality badge */}
          {dataQuality && (
            <div className="mt-4 text-center">
              <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                Data: {dataQuality}
              </span>
            </div>
          )}
        </div>

        {/* Coach Briefing Card */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Coach Briefing</h3>
            {statusBadge(briefing.status || null)}
          </div>

          {/* Summary */}
          {briefing.summary && (
            <p className="text-gray-700 mb-4">{briefing.summary}</p>
          )}

          {/* Flags */}
          {briefing.flags && briefing.flags.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 mb-2">Flags</p>
              <div className="flex flex-wrap gap-2">
                {briefing.flags.map((flag: string, i: number) => (
                  <span key={i} className="inline-flex px-2.5 py-1 text-xs rounded-full bg-orange-100 text-orange-700">
                    {flag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Suggested Action */}
          {briefing.suggested_action && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <p className="text-xs font-medium text-indigo-600 mb-1">Suggested Action</p>
              <p className="text-sm text-indigo-900">{briefing.suggested_action}</p>
            </div>
          )}

          {/* Empty briefing state */}
          {!briefing.summary && !briefing.suggested_action && (
            <p className="text-gray-400 text-sm italic">No briefing content available.</p>
          )}
        </div>
      </div>

      {/* User Nudges Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-500 mb-4">
          User Nudges
          <span className="ml-2 text-xs text-gray-400">What the user sees</span>
        </h3>

        {nudges.length > 0 ? (
          <div className="space-y-3">
            {nudges.map((nudge, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0 mt-0.5">
                  {nudgeTypeBadge(nudge.type || 'suggestion')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800">{nudge.message}</p>
                </div>
                {nudge.priority && (
                  <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded ${
                    nudge.priority === 'high' ? 'bg-red-100 text-red-700' :
                    nudge.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {nudge.priority}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-sm italic">No nudges generated for this client.</p>
        )}
      </div>
    </div>
  );
};

// --- Main Export ---

export const AgentInsightsPage: React.FC<AgentInsightsPageProps> = ({ user, coachMode, clientId, clientName }) => {
  if (clientId) {
    return <ClientInsightDetail clientId={clientId} clientName={clientName || 'Client'} />;
  }
  return <AllClientsOverview />;
};
